import type {
  AlertRuleSpec,
  AlertTrigger,
  DensityStateView,
  InstrumentRef,
  LibraryRoundGroups,
  LibrarySupervision,
  LostCauseView,
  MarketKind,
  OutcomeShares,
  RoundCutPrice,
  SideView,
  SituationKindView,
  WrappedCoin,
} from "@screener/protocol";
import type { DensitySortKey } from "@/density/derive";
import type { RibbonOutcome } from "@/density/stream-state";

/**
 * Единственное место подписей клиента (stage6-acceptance-debts, design.md
 * решение 9; решение владельца от 28.09.2026 «Одни подписи на всех экранах
 * клиента»). Экран, которому нужна подпись, берёт её отсюда, а не заводит
 * свою копию — копия расходится с оригиналом при первой же правке. Каждая
 * карта — `Record` по значению контракта: новое значение `@screener/protocol`
 * обязано ломать сборку, а не показываться сырым.
 */

const MARKET_LABELS: Record<MarketKind, string> = {
  spot: "SPOT",
  perp: "PERP",
};

/** Рынок §3.2 словом — карточка называет его так же, как объясняет §9 (1.21). */
export function marketLabel(market: MarketKind): string {
  return MARKET_LABELS[market];
}

/** Строка — известное значение `MarketKind` (ключи карты подписей исчерпывающи). */
export function isMarketKind(value: string): value is MarketKind {
  return Object.hasOwn(MARKET_LABELS, value);
}

/**
 * Значения рынка контракта в порядке §3.2 — источник выпадающего списка
 * формы правила (`rule-form-sheet.tsx`): ключи `MARKET_LABELS` исчерпывающи
 * по построению (`Record<MarketKind, string>`), и список вариантов не
 * заводит собственной копии перечня.
 */
export const MARKET_KINDS: readonly MarketKind[] = Object.keys(MARKET_LABELS) as MarketKind[];

const SIDE_LABELS: Record<SideView, string> = {
  bid: "BID",
  ask: "ASK",
};

export function sideLabel(side: SideView): string {
  return SIDE_LABELS[side];
}

/** Стороны стакана контракта — тот же приём, что `MARKET_KINDS`. */
export const SIDES: readonly SideView[] = Object.keys(SIDE_LABELS) as SideView[];

/**
 * Биржи, адаптеры которых есть у демона, с точным написанием бренда —
 * наивная капитализация первой буквы даёт «Okx» вместо «OKX» (решение
 * владельца 28.09.2026, второй круг). Карта общая с `ExchangeLogo`
 * (`exchange-logo.tsx`): знак биржи несёт то же имя в `alt`, что
 * подпись здесь, — расхождение было бы видно тут же рядом со знаком.
 * «Hyperliquid» — решение владельца 06.10.2026 (ТЗ пишет «HyperLiquid»).
 * Coinbase и Kraken добавлены change'ем `stage8-exchange-labels` (этап 8).
 * Восемь бирж этапа 9 (ТЗ 1.79, §92) — DEN-52; написание по §92–§100.
 */
export const EXCHANGE_NAMES = {
  binance: "Binance",
  bybit: "Bybit",
  okx: "OKX",
  hyperliquid: "Hyperliquid",
  coinbase: "Coinbase",
  kraken: "Kraken",
  mexc: "MEXC",
  gate: "Gate",
  lighter: "Lighter",
  bitget: "Bitget",
  kucoin: "KuCoin",
  bingx: "BingX",
  htx: "HTX",
  aster: "AsterDex",
} as const;

export type KnownExchange = keyof typeof EXCHANGE_NAMES;

/**
 * Узнаёт биржу очереди §3.1 по карте `EXCHANGE_NAMES` — общая проверка для
 * подписи (`exchangeLabel`) и знака (`ExchangeLogo`, `exchange-logo.tsx`):
 * своей копии там нет, известность биржи определяется в одном месте.
 */
export function isKnownExchange(exchange: string): exchange is KnownExchange {
  // `Object.hasOwn`, а не `in`: `in` проходит и по унаследованным свойствам
  // прототипа (`toString`, `constructor` и подобные), и строка с таким именем
  // биржи ошибочно читалась бы как известная.
  return Object.hasOwn(EXCHANGE_NAMES, exchange);
}

/**
 * Биржа в контракте — незакрытая строка (`InstrumentRef.exchange: string`),
 * не перечисление контракта: точного `Record<Exchange, string>` не
 * построить, и компилятор его полноту не проверит. Известная биржа — своё
 * написание из `EXCHANGE_NAMES`, прочая — строка как есть (та же запасная
 * форма, что рисует `ExchangeLogo` для неизвестной биржи).
 */
export function exchangeLabel(exchange: string): string {
  return isKnownExchange(exchange) ? EXCHANGE_NAMES[exchange] : exchange;
}

/**
 * Приведённая пара §3.2 через косую черту — «BTC/USDT», не тикер биржи
 * («BTCUSDT»). Только для групп с нескольких рынков (строка группы
 * ситуаций, где инструмент не один) и мест без инструмента. Инструмент на
 * экране пишет `tickerLabel` (§63). Библиотека этой функцией не пользуется.
 * Принимает `{ base, quote }` структурно, не весь `InstrumentRef`: ключ
 * ситуации §5 (`SituationKeyView`) несёт пару без биржи и рынка, и своей
 * копии сборки строки заводить не должен (ревью stage6-acceptance-debts,
 * второй круг, пункт 2).
 */
export function normalizedPairLabel(pair: Pick<InstrumentRef, "base" | "quote">): string {
  return `${pair.base}/${pair.quote}`;
}

/**
 * Единственная запись инструмента на экране (§63, §57): база в написании
 * биржи и котировка — на споте через косую черту («BTC/USDT»), на
 * бессрочном слитно («1000PEPEUSDT», «kPEPEUSDC»). Тем же правилом «копировать
 * тикер» кладёт строку в буфер, поэтому экран и буфер совпадают по
 * построению. База биржи неизвестна (писатель прежней версии) — пустая
 * строка, откат на приведённую `instrument.base`.
 *
 * Дубль правила — `ticker_label` демона (Telegram); правится парой.
 */
export function tickerLabel(
  instrument: Pick<InstrumentRef, "market" | "base" | "quote">,
  exchangeBase: string,
): string {
  const base = exchangeBase !== "" ? exchangeBase : instrument.base;
  return instrument.market === "spot"
    ? `${base}/${instrument.quote}`
    : `${base}${instrument.quote}`;
}

/**
 * Пометка обёрнутой монеты §3.2 1.27 — «обёрнутый BTC» у `UBTC`. Единственное
 * место её текста: экраны берут пометку отсюда, а не собирают слово сами.
 * Монета вне перечня приветствия пометки не несёт. Тикер пометка не
 * заменяет — инструмент по-прежнему называется `tickerLabel`.
 */
export function wrappedMark(base: string, wrapped: readonly WrappedCoin[]): string | undefined {
  const entry = wrapped.find((candidate) => candidate.coin === base);
  return entry === undefined ? undefined : `обёрнутый ${entry.underlying}`;
}

/** Биржа и рынок вместе — «Binance · PERP», как называют таблица и карточка. */
export function venueOf(instrument: InstrumentRef): string {
  return `${exchangeLabel(instrument.exchange)} · ${marketLabel(instrument.market)}`;
}

/**
 * Состояния FSM плотности §4.4 словом — то же слово называет таблица,
 * карточка, детальная панель, фильтр и триггер алерта (`ALERT_TRIGGER_LABELS`
 * ниже, ревью stage6-acceptance-debts, второй круг, пункт 3): своей копии
 * слов Confirmed/Eaten/… не заводится нигде, кроме этой карты.
 */
const STATE_LABELS: Record<DensityStateView, string> = {
  detected: "Detected",
  pending: "Pending",
  confirmed: "Confirmed",
  approached: "Approached",
  tested: "Tested",
  held: "Held",
  eaten: "Eaten",
  pulled: "Pulled",
  vanished: "Vanished",
  lost: "Lost",
  stopped: "Stopped",
};

export function stateLabel(state: DensityStateView): string {
  return STATE_LABELS[state];
}

/**
 * История стены в окне плотности: три разных утверждения словами пользователя.
 * «Стены в библиотеке нет», «история не прочитана» и «подходов не было» —
 * разные факты, и общая подпись на двоих врала бы.
 */
export const LIBRARY_HISTORY_LABELS = {
  /** Записей библиотеки у стены нет: она ниже порога библиотеки или ещё не простояла достаточно. */
  noRecords: "В библиотеке этой стены нет.",
  /** Библиотека не ответила; живые величины от неё не зависят. */
  unavailable:
    "Историю прочитать не удалось — библиотека сайзов недоступна. Это не значит, что записей или подходов не было.",
  /** Запись есть, закрытых подходов у неё нет. */
  noApproaches: "Закрытых подходов в библиотеке нет.",
  /** Исход живой записи: он ещё не наступил, пустотой его не показываем. */
  recordOngoing: "ещё идёт",
} as const;

/** Исход ленты §9 — то же слово, что одноимённое состояние FSM (`stateLabel`). */
const OUTCOME_LABELS: Record<RibbonOutcome, string> = {
  eaten: STATE_LABELS.eaten,
  pulled: STATE_LABELS.pulled,
  lost: STATE_LABELS.lost,
};

export function outcomeLabel(outcome: RibbonOutcome): string {
  return OUTCOME_LABELS[outcome];
}

/** Причина потери плотности из события перехода в `Lost` (§4.4). */
const LOST_CAUSE_LABELS: Record<LostCauseView, string> = {
  stream_gap: "связь с биржей",
  beyond_known_book: "за краем книги",
};

export function lostCauseLabel(cause: LostCauseView): string {
  return LOST_CAUSE_LABELS[cause];
}

/**
 * Подписи триггера алерта §6.5 для нотификации §8 (1.20) и экрана «Алерты».
 * Шесть из восьми триггеров — имена состояний FSM §4.4 и называются тем же
 * словом, что `stateLabel` (`STATE_LABELS` выше) — не своей копией.
 * `moved` состоянием FSM не является, `situation_stacked` — «Завал»
 * (решение владельца 28.09.2026).
 */
const ALERT_TRIGGER_LABELS: Record<AlertTrigger, string> = {
  confirmed: STATE_LABELS.confirmed,
  approached: STATE_LABELS.approached,
  tested: STATE_LABELS.tested,
  held: STATE_LABELS.held,
  eaten: STATE_LABELS.eaten,
  pulled: STATE_LABELS.pulled,
  moved: "Moved",
  situation_stacked: "Завал",
};

export function alertTriggerLabel(trigger: AlertTrigger): string {
  return ALERT_TRIGGER_LABELS[trigger];
}

/**
 * Подписи типа ситуации §5 — `stacked` называется так же, как одноимённый
 * триггер алерта («Завал»); `single` — «Одиночная» (1.51, §64 п. 4).
 */
const SITUATION_KIND_LABELS: Record<SituationKindView, string> = {
  stacked: "Завал",
  single: "Одиночная",
};

export function situationKindLabel(kind: SituationKindView): string {
  return SITUATION_KIND_LABELS[kind];
}

/**
 * Четыре действия правила §6.5/§8 — контрактные ключи `AlertRuleSpec`
 * (`Pick`, а не свой список): новое действие в контракте не типизируется
 * этим ключом молча.
 */
export type AlertActionKey = keyof Pick<AlertRuleSpec, "sound" | "notify" | "telegram" | "log">;

const ACTION_LABELS: Record<AlertActionKey, string> = {
  sound: "Звук",
  notify: "Уведомления",
  telegram: "Telegram",
  log: "Лог",
};

export function actionLabel(action: AlertActionKey): string {
  return ACTION_LABELS[action];
}

const SORT_KEY_LABELS: Record<DensitySortKey, string> = {
  usd: "Размер",
  age: "Время жизни",
  distance: "Дистанция",
};

export function sortKeyLabel(key: DensitySortKey): string {
  return SORT_KEY_LABELS[key];
}

/**
 * Заголовки колонок таблицы плотностей §9 — по-русски целиком (решение
 * владельца 28.09.2026, второй круг): три сортируемые колонки уже называет
 * `sortKeyLabel`, здесь — оставшиеся восемь, общие у `DensityTable`,
 * `DensityCoinTable` и `SituationList` (`density-table-header.tsx`).
 */
export type DensityColumnKey =
  | "venue"
  | "pair"
  | "side"
  | "anchor"
  | "levels"
  | "state"
  | "approaches"
  | "flags";

const DENSITY_COLUMN_LABELS: Record<DensityColumnKey, string> = {
  venue: "Биржа · рынок",
  pair: "Пара",
  side: "Сторона",
  anchor: "Якорь",
  levels: "Ур.",
  state: "Состояние",
  approaches: "Подх.",
  flags: "Флаги",
};

export function densityColumnLabel(key: DensityColumnKey): string {
  return DENSITY_COLUMN_LABELS[key];
}

/**
 * Полное слово для сокращённого заголовка — подсказка при наведении:
 * сокращение экономит ширину колонки, а не прячет смысл. Формулировка
 * «уровней в кластере» — из §9 спецификации, тем же словом называет и
 * детальная панель («Уровней», `density-detail-content.tsx`).
 */
const DENSITY_COLUMN_HINTS: Partial<Record<DensityColumnKey, string>> = {
  levels: "Уровней в кластере",
  approaches: "Подходов",
};

export function densityColumnHint(key: DensityColumnKey): string | undefined {
  return DENSITY_COLUMN_HINTS[key];
}

/** Русское числительное по правилу «11–14 → many, иначе по последней цифре». */
export function pluralForm(count: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(count) % 100;
  if (mod100 >= 11 && mod100 <= 14) {
    return many;
  }
  const mod10 = Math.abs(count) % 10;
  if (mod10 === 1) {
    return one;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }
  return many;
}

/**
 * Подписи цены разреза по круглости §9 (1.8, 1.28) — по значению ответа
 * демона, а не по месту разреза на экране. Термины — цены §6.1: плотность
 * могла стоять до того, как переросла порог, и система знает только, когда
 * её обнаружила.
 */
const ROUND_CUT_PRICE_LABELS: Record<RoundCutPrice, { title: string; hint: string }> = {
  anchor_at_detection: {
    title: "По цене обнаружения",
    hint: "якорь, когда плотность обнаружена",
  },
  anchor_at_end: {
    title: "По цене завершения",
    hint: "якорь, когда плотность завершилась",
  },
};

export function roundCutPriceLabel(price: RoundCutPrice): string {
  return ROUND_CUT_PRICE_LABELS[price].title;
}

export function roundCutPriceHint(price: RoundCutPrice): string {
  return ROUND_CUT_PRICE_LABELS[price].hint;
}

/**
 * Тип-ловушка полноты перечня: не собирается, если в перечне нет какого-то
 * значения контракта (`Missing` не `never`). Рантайма не несёт.
 */
type AssertComplete<Missing extends never> = Missing;

/** Группы разреза в порядке строк таблицы — от некруглых к самой круглой. */
export const ROUND_GROUPS = [
  "off_grid",
  "whole",
  "half",
  "fine",
] as const satisfies readonly (keyof LibraryRoundGroups)[];

export type RoundGroup = (typeof ROUND_GROUPS)[number];
export type RoundGroupsComplete = AssertComplete<Exclude<keyof LibraryRoundGroups, RoundGroup>>;

/**
 * Подпись группы без ранга. Слова трёх рангов здесь не заводятся: их
 * называет `roundRankLabel` (`density/format.ts`), тот же источник, что у
 * отметки круглой цены в строке плотности.
 */
export const OFF_GRID_LABEL = "Некруглые";

/** Досмотренность §9 (1.16) — подписи сегментов фильтра. */
const SUPERVISION_LABELS: Record<LibrarySupervision, string> = {
  all: "Все",
  observed: "Досмотренные",
  unobserved: "Недосмотренные",
};

export function supervisionLabel(supervision: LibrarySupervision): string {
  return SUPERVISION_LABELS[supervision];
}

/** Сегменты фильтра в порядке показа; полнота — по значениям контракта. */
export const SUPERVISIONS = [
  "all",
  "observed",
  "unobserved",
] as const satisfies readonly LibrarySupervision[];
export type SupervisionsComplete = AssertComplete<
  Exclude<LibrarySupervision, (typeof SUPERVISIONS)[number]>
>;

/** Шесть исходов подходов §6.2 в порядке §9 (1.5) — те же слова, что у состояний FSM. */
const OUTCOME_SHARE_LABELS: Record<keyof OutcomeShares, string> = {
  held: STATE_LABELS.held,
  eaten: STATE_LABELS.eaten,
  pulled: STATE_LABELS.pulled,
  timeout: "Timeout",
  lost: STATE_LABELS.lost,
  stopped: STATE_LABELS.stopped,
};

export function outcomeShareLabel(outcome: keyof OutcomeShares): string {
  return OUTCOME_SHARE_LABELS[outcome];
}

/** Исходы в порядке §9 (1.5); полнота — по полям контракта. */
export const OUTCOME_SHARES = [
  "held",
  "eaten",
  "pulled",
  "timeout",
  "lost",
  "stopped",
] as const satisfies readonly (keyof OutcomeShares)[];
export type OutcomeSharesComplete = AssertComplete<
  Exclude<keyof OutcomeShares, (typeof OUTCOME_SHARES)[number]>
>;

/**
 * Исход недосмотренного подхода §9 (1.16): разрыв потока и штатная остановка
 * демона. Карта по значению контракта — новый исход обязан решить, к какому
 * виду он относится, а не остаться молча досмотренным.
 */
const OUTCOME_SHARE_UNOBSERVED: Record<keyof OutcomeShares, boolean> = {
  held: false,
  eaten: false,
  pulled: false,
  timeout: false,
  lost: true,
  stopped: true,
};

export function isUnobservedOutcome(outcome: keyof OutcomeShares): boolean {
  return OUTCOME_SHARE_UNOBSERVED[outcome];
}

/**
 * Пояснение к счётчику плотностей: плотности, чей собственный исход `Lost`
 * или `Stopped` (§4.4), в него не входят, хотя их подходы в агрегаты входят.
 */
export const DENSITIES_COUNT_HINT = "без потерянных и остановленных";

/** Словесная пометка недосмотренного исхода — рядом с именем, не вместо него и не цветом. */
export const UNOBSERVED_MARK = "недосмотрен";

const USD_SUFFIXES: Array<{ limit: number; suffix: string }> = [
  { limit: 1_000_000_000, suffix: "B" },
  { limit: 1_000_000, suffix: "M" },
  { limit: 1_000, suffix: "K" },
];

/**
 * Краткая запись объёма в USD. Точное значение остаётся в подсказке строки:
 * сокращение — для чтения колонки глазом, а не вместо числа.
 */
export function formatUsd(value: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return value;
  }
  for (const { limit, suffix } of USD_SUFFIXES) {
    if (Math.abs(amount) >= limit) {
      const scaled = amount / limit;
      return `${scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)}${suffix}`;
    }
  }
  return amount.toFixed(0);
}

const CHIP_UNITS: ReadonlyArray<readonly [number, string]> = [
  [1_000_000_000, "B"],
  [1_000_000, "M"],
  [1_000, "K"],
];

/**
 * Краткая запись границы для фишки полосы: «25K», «1.25M». Граница отбора не
 * округляется — фишка «≥ 1.3M» при пороге 1 250 000 противоречила бы строке
 * 1.26M на экране. Не укладывается в два знака после точки — пишется целиком.
 */
export function formatChipUsd(amount: number): string {
  for (const [unit, suffix] of CHIP_UNITS) {
    if (amount >= unit) {
      const hundredths = (amount * 100) / unit;
      if (!Number.isInteger(hundredths)) {
        return groupThousands(String(Math.trunc(amount)));
      }
      return `${(hundredths / 100).toFixed(2).replace(/\.?0+$/, "")}${suffix}`;
    }
  }
  return String(amount);
}

/** Быстрые значения «Размер от» (USD); подписи — `formatChipUsd`. */
export const MIN_USD_QUICK_VALUES: readonly number[] = [
  100_000, 250_000, 500_000, 1_000_000, 5_000_000,
];

/** Быстрое значение, точно равное размеру фильтра, иначе `null`. */
export function selectedQuickMinUsd(minUsd: number | null): number | null {
  return MIN_USD_QUICK_VALUES.find((value) => value === minUsd) ?? null;
}

/** Нажатие на быстрое значение: невыбранное задаёт размер, выбранное снимает его. */
export function toggleQuickMinUsd(minUsd: number | null, value: number): number | null {
  return minUsd === value ? null : value;
}

/** Узкий пробел (U+2009) — разделитель разрядов счётчиков панели: не шире цифры. */
const COUNT_SEPARATOR = "\u2009";

/** Число счётчика панели с узким пробелом между разрядами: 13798 → «13 798». */
export function formatCount(value: number): string {
  return groupThousands(String(value)).replaceAll(THOUSANDS_SEPARATOR, COUNT_SEPARATOR);
}

/** Кнопка раскрытия панели фильтров: число заданных фильтров входит в доступное имя. */
export function filtersButtonLabel(activeCount: number): string {
  return activeCount > 0 ? `Фильтры, задано ${activeCount}` : "Фильтры";
}

/**
 * Подписи панели фильтров (ТЗ 1.97, §110): фишки строки, шапки разделов,
 * пояснения и нижняя строка. Одно место, как и остальные подписи клиента.
 */
export const FILTER_TEXT = {
  sizeTag: "Размер ≥",
  stagesTag: "Стадии",
  exchangesTag: "Биржи",
  noExchanges: "ни одной",
  hideMmTag: { label: "ММ", value: "скрыты" },
  onlyFavoritesTag: { label: "Избранное", value: "только" },
  removeSize: "Снять фильтр размера",
  removeStages: "Снять фильтр стадий",
  removeExchanges: "Снять фильтр бирж",
  removeHideMm: "Снять фильтр ММ",
  removeOnlyFavorites: "Снять фильтр избранного",
  sizeTitle: "Размер от",
  sizePlaceholder: "любой",
  sizeCurrency: "USD",
  sizeReset: "Сбросить",
  sizeHint: "Текущий объём в стакане, не пиковый за жизнь плотности.",
  stageTitle: "Стадия",
  stageReset: "Все стадии",
  stageHint: "Терминальные стадии уходят из таблицы сразу, их исход — в ленте внизу.",
  exchangeTitle: "Биржа",
  exchangeAll: "все",
  exchangeReset: "Все биржи",
  marksTitle: "Пометки",
  hideMm: "Скрыть ММ",
  hideMmHint: "Заявки с признаками маркетмейкера",
  onlyFavorites: "Только избранные",
  onlyFavoritesHint: "Монеты со звёздочкой",
  footerNote: "Фильтры применяются сразу и запоминаются в браузере.",
  resetAll: "Сбросить всё",
} as const;

/** Сводка раздела «Биржа»: «все» без отбора, иначе «N из M» — по рынкам (§115 п. 4). */
export function exchangeSummary(selected: number, total: number): string {
  return `${selected} из ${total}`;
}

/** Буква рынка на кнопке раздела «Биржа» и в фишке свёрнутой строки (§115). */
const MARKET_LETTERS: Record<MarketKind, string> = {
  spot: "S",
  perp: "P",
};

export function marketLetter(market: MarketKind): string {
  return MARKET_LETTERS[market];
}

const MARKET_TOGGLE_NAMES: Record<MarketKind, string> = {
  spot: "Спот",
  perp: "Бессрочные",
};

/** Доступное имя кнопки рынка: «Спот Bybit» / «Бессрочные Bybit». */
export function marketToggleLabel(market: MarketKind, exchange: string): string {
  return `${MARKET_TOGGLE_NAMES[market]} ${exchangeLabel(exchange)}`;
}

/** Состояние рынков биржи в отборе: все включены, часть, ни одного. */
export type ExchangeToggleState = "all" | "some" | "none";

const EXCHANGE_TOGGLE_STATE_TEXT: Record<ExchangeToggleState, string> = {
  all: "все рынки",
  some: "часть рынков",
  none: "рынки выключены",
};

/** Доступное имя кнопки-имени биржи: «Binance: все рынки» (§115 п. 2). */
export function exchangeToggleLabel(exchange: string, state: ExchangeToggleState): string {
  return `${exchangeLabel(exchange)}: ${EXCHANGE_TOGGLE_STATE_TEXT[state]}`;
}

/** Фишка полосы фильтров: подпись и, если настройка задана, значение (выделяется отдельно). */
export type ChipParts = { label: string; value?: string };

/** Фишка одной строкой: «подпись значение». */
export function chipPartsText(parts: ChipParts): string {
  return parts.value === undefined ? parts.label : `${parts.label} ${parts.value}`;
}

/** Неразрывный пробел (U+00A0) — разделитель разрядов, не обычный пробел. */
const THOUSANDS_SEPARATOR = " ";

/** Группирует целую часть по три цифры справа неразрывным пробелом. */
export function groupThousands(intPart: string): string {
  const reversedChars = intPart.split("").reverse();
  const groups: string[] = [];
  for (let i = 0; i < reversedChars.length; i += 3) {
    groups.push(
      reversedChars
        .slice(i, i + 3)
        .reverse()
        .join(""),
    );
  }
  return groups.reverse().join(THOUSANDS_SEPARATOR);
}

/**
 * Форматирует уже очищенную десятичную строку `$<разряды>[.<дробь>]`.
 *
 * Не `formatUsd`: та даёт краткую запись для колонки таблицы («1.2M»), а
 * строка фильтра правила называет точную границу («$2 000 000») — разное
 * чтение одной величины, живущее здесь же рядом, не дубликат в другом файле.
 */
export function formatUsdExact(amount: string): string {
  const [intPart, fracPart] = amount.split(".");
  const grouped = groupThousands(intPart ?? "0");
  return fracPart === undefined ? `$${grouped}` : `$${grouped}.${fracPart}`;
}

/**
 * Подписи личных минимумов объёма §59 (change stage8-personal-display-filters):
 * пункт меню, окно, пометки и ошибки. Одни слова на экране плотностей и в окне.
 */
/** Заголовок окна всех минимумов (открывается кнопкой полосы фильтров, stage8-card-minimum). */
export const DISPLAY_MINIMUMS_MENU_LABEL = "Минимальный объём";
export const DISPLAY_MINIMUMS_MARKETS_HEADING = "Рынки";
export const DISPLAY_MINIMUMS_COINS_HEADING = "Монеты";
const MINIMUMS_BUTTON_LABEL = "Мин. объём";

/**
 * Подпись кнопки «Мин. объём» в полосе фильтров (stage8-card-minimum, Д2):
 * число действующих минимумов, «недоступны» при ошибке чтения, голая подпись
 * пока читается или действующих нет. `count` — `activeMinimumsCount`.
 */
export function minimumsButtonParts(count: number | null, unavailable: boolean): ChipParts {
  if (unavailable) {
    return { label: `${MINIMUMS_BUTTON_LABEL} · недоступны` };
  }
  return count !== null && count > 0
    ? { label: MINIMUMS_BUTTON_LABEL, value: String(count) }
    : { label: MINIMUMS_BUTTON_LABEL };
}

/** Надпись полосы фильтров, когда время до показа §82 прочитать не удалось. */
export const DISPLAY_MIN_AGE_UNAVAILABLE_TEXT = "время до показа недоступно";

/**
 * Подписи времени до показа §82 (change stage9-display-min-age): кнопка полосы
 * фильтров и её окно. Число называется на кнопке только когда настройка задана.
 */
const DISPLAY_MIN_AGE_BUTTON_LABEL = "Показ";

export function displayMinAgeButtonParts(
  minAgeSec: number | null,
  unavailable: boolean,
  loading: boolean,
): ChipParts {
  if (unavailable) {
    return { label: `${DISPLAY_MIN_AGE_BUTTON_LABEL} · ${DISPLAY_MIN_AGE_UNAVAILABLE_TEXT}` };
  }
  if (loading) {
    return { label: `${DISPLAY_MIN_AGE_BUTTON_LABEL}: читаем…` };
  }
  return minAgeSec === null
    ? { label: `${DISPLAY_MIN_AGE_BUTTON_LABEL}: не задано` }
    : { label: `${DISPLAY_MIN_AGE_BUTTON_LABEL} от`, value: `${minAgeSec} с` };
}

export const DISPLAY_MIN_AGE_TITLE = "Время до показа";
export const DISPLAY_MIN_AGE_FIELD_LABEL = "Секунд до показа";
export const DISPLAY_MIN_AGE_EXPLANATION =
  "Плотность появляется на экране, когда ей исполнилось столько секунд. Неподтверждённые старше этого времени видны тускло, подтверждённые моложе него скрыты.";
export const DISPLAY_MIN_AGE_LOADING = "Читаем время до показа…";
export const DISPLAY_MIN_AGE_BAD_NUMBER = "Введите целое число секунд, 0 или больше.";
export const DISPLAY_MIN_AGE_SAVE_FAILED = "Не удалось сохранить, попробуйте ещё раз";
export const DISPLAY_MIN_AGE_REMOVE_FAILED = "Не удалось убрать, попробуйте ещё раз";
export const DISPLAY_MIN_AGE_REJECTED = "Сервер не принял это значение — проверьте число.";

/** Заголовок окна минимума монеты на карточке (§62 п. 2). */
export function cardMinimumTitle(instrument: InstrumentRef): string {
  return `Минимум для ${instrument.base} · ${exchangeLabel(instrument.exchange)} · ${marketLabel(instrument.market)}`;
}

/** Подпись под заголовком: ключ минимума монеты без котировки (§59 п. 1). */
export function cardMinimumCaption(instrument: InstrumentRef): string {
  return `действует на все пары ${instrument.base} на ${exchangeLabel(instrument.exchange)} · ${marketLabel(instrument.market)}`;
}

/** Число в шапке карточки: «от 5.0M». */
export function cardMinimumValueText(minUsd: string): string {
  return `от ${formatUsd(minUsd)}`;
}

/** Подсказка и текстовая альтернатива значка порога: действие и минимум словами. */
const CARD_MINIMUM_NOT_READ_HINT = "минимумы не прочитаны";
/** Окно значка порога, пока список читается: поля и кнопок записи нет. */
export const CARD_MINIMUM_LOADING = "Минимумы читаются…";

export function cardMinimumTriggerLabel(
  base: string,
  minimum:
    | { status: "unread" }
    | { status: "none" }
    | { status: "set"; minUsd: string; belowFloor: boolean },
): string {
  const action = `Минимум для ${base}`;
  if (minimum.status === "unread") {
    return `${action}: ${CARD_MINIMUM_NOT_READ_HINT}`;
  }
  if (minimum.status === "none") {
    return `${action}: не задан — задать`;
  }
  const value = `${action}: ${cardMinimumValueText(minimum.minUsd)}`;
  return minimum.belowFloor
    ? `${value} — ниже порога скринера, не действует`
    : `${value} — изменить`;
}
export const DISPLAY_MINIMUMS_LOADING = "Читаем личные минимумы…";
export const DISPLAY_MINIMUMS_LOAD_FAILED =
  "Не удалось прочитать личные минимумы. Пока список не прочитан, строки по нему не отбираются.";
export const DISPLAY_MINIMUMS_RETRY = "Повторить";
export const DISPLAY_MINIMUMS_SAVE = "Сохранить";
export const DISPLAY_MINIMUMS_REMOVE = "Убрать";
export const DISPLAY_MINIMUMS_ADD = "Добавить";
export const DISPLAY_MINIMUMS_NO_COINS = "Минимумов монет не задано.";
export const DISPLAY_MINIMUMS_NO_MARKETS = "Рынков в потоке пока нет.";
/** Подсказка у нечитаемой суммы в долларах (§66 п. 3). */
export const AMOUNT_HINT = "Введите сумму в долларах, например 1500 или 1,5к";
/** Подсказка у суммы, которая обязана быть больше нуля (личные минимумы). */
export const POSITIVE_AMOUNT_HINT = "Введите сумму больше нуля, например 1500 или 1,5к";
/** Негодное число: не число или не больше нуля. Запрос не уходит. */
export const DISPLAY_MINIMUMS_BAD_NUMBER = POSITIVE_AMOUNT_HINT;
export const DISPLAY_MINIMUMS_BAD_COIN = "Введите монету.";
/** Сервер отклонил запись как негодную: повторять тот же запрос нет смысла. */
export const DISPLAY_MINIMUMS_REJECTED = "Сервер не принял это значение — проверьте число.";
/** Хранилище не ответило: введённое число остаётся в поле. */
export const DISPLAY_MINIMUMS_SAVE_FAILED = "Не удалось сохранить, попробуйте ещё раз";
/** Хранилище не ответило на снятие записи. */
export const DISPLAY_MINIMUMS_REMOVE_FAILED = "Не удалось убрать, попробуйте ещё раз";
/** Сервер отклонил снятие как негодный запрос: числа в нём нет, на число не ссылаемся. */
export const DISPLAY_MINIMUMS_REMOVE_REJECTED = "Сервер не принял запрос";
/** Одна подпись под заголовком «Рынки»: что значит пустое поле. */
export const DISPLAY_MINIMUMS_MARKETS_HINT = "Пустое поле — минимума нет";
/** Плейсхолдер пустого поля минимума рынка. */
export const DISPLAY_MINIMUMS_EMPTY_PLACEHOLDER = "—";
/** Выбранной пары в форме добавления монеты нет (исчезла из потока). */
export const DISPLAY_MINIMUMS_PICK_PAIR = "Выберите биржу и рынок";

/** Минимум ниже порога скринера на рынке не действует (§59 п. 2); порог назван числом. */
export function belowDaemonFloorLabel(floorUsd: string): string {
  return `Ниже порога скринера ${formatUsd(floorUsd)} на этом рынке — не действует`;
}
